import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Link,
  Button,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

const colors = {
  black: '#000000',
  darkRed: '#451a15',
  red: '#970000',
  yellow: '#fdb200',
  offWhite: '#f6ede7',
};

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Privacy Policy - Ulpian</title>
        <meta name="description" content="Ulpian's Privacy Policy - How we collect, use, and protect your personal information" />
      </Helmet>

      <Box sx={{
        minHeight: '100vh',
        backgroundColor: colors.offWhite,
        pt: 10,
        pb: 6
      }}>
        <Container maxWidth="md">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{
              mb: 3,
              color: colors.darkRed,
              '&:hover': {
                backgroundColor: colors.yellow + '20',
              }
            }}
          >
            Back to Home
          </Button>

          <Paper sx={{ p: { xs: 3, md: 6 }, backgroundColor: 'white' }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                mb: 4,
                color: colors.darkRed,
                fontFamily: 'TanAshford, serif',
                fontWeight: 'bold'
              }}
            >
              Privacy Policy
            </Typography>

            <Typography variant="body2" sx={{ mb: 4, color: colors.darkRed }}>
              Last updated: {new Date().toLocaleDateString()}
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: colors.darkRed }}>
                1. Introduction
              </Typography>
              <Typography paragraph>
                ULPIAN AI ("we," "our," or "us") is committed to protecting your privacy. This Privacy
                Policy explains how we collect, use, disclose, and safeguard your information when you
                use our web-based application and website (collectively, the "Service").
              </Typography>
              <Typography paragraph>
                Please read this Privacy Policy carefully. By using the Service, you agree to the
                collection and use of information in accordance with this policy.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: colors.darkRed }}>
                2. Information We Collect
              </Typography>

              <Typography variant="h6" sx={{ mt: 3, mb: 1, color: colors.red }}>
                Personal Information
              </Typography>
              <Typography paragraph>
                We may collect personal information that you provide directly to us, such as:
              </Typography>
              <ul>
                <li>Name and contact information (email address, phone number)</li>
                <li>Account credentials</li>
                <li>Professional information (company, job title)</li>
                <li>Payment information</li>
                <li>Communications with us</li>
              </ul>

              <Typography variant="h6" sx={{ mt: 3, mb: 1, color: colors.red }}>
                Usage Information
              </Typography>
              <Typography paragraph>
                We automatically collect certain information when you use our Service:
              </Typography>
              <ul>
                <li>Log data (IP address, browser type, operating system)</li>
                <li>Device information</li>
                <li>Usage patterns and preferences</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: colors.darkRed }}>
                3. How We Use Your Information
              </Typography>
              <Typography paragraph>
                We use the information we collect for various purposes:
              </Typography>
              <ul>
                <li>To provide and maintain our Service</li>
                <li>To notify you about changes to our Service</li>
                <li>To provide customer support</li>
                <li>To gather analysis or valuable information to improve our Service</li>
                <li>To monitor the usage of our Service</li>
                <li>To detect, prevent and address technical issues</li>
                <li>To fulfill legal obligations</li>
              </ul>
              <Typography paragraph sx={{ mt: 2, fontWeight: 'bold' }}>
                Important: We never use your data to train, retrain, or improve our AI models. Your legal queries, documents, and research remain completely confidential.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: colors.darkRed }}>
                4. Legal Basis for Processing
              </Typography>
              <Typography paragraph>
                We process your personal data on the following legal bases:
              </Typography>
              <ul>
                <li><strong>Consent:</strong> You have given consent for specific purposes</li>
                <li><strong>Contract:</strong> Processing is necessary for our contract with you</li>
                <li><strong>Legal obligations:</strong> Processing is necessary to comply with law</li>
                <li><strong>Legitimate interests:</strong> Processing is necessary for our legitimate interests</li>
              </ul>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: colors.darkRed }}>
                5. Data Sharing and Disclosure
              </Typography>
              <Typography paragraph>
                We may share your information in the following circumstances:
              </Typography>
              <ul>
                <li>With service providers who assist us in operating our Service</li>
                <li>To comply with legal obligations</li>
                <li>To protect our rights and safety</li>
                <li>With your consent</li>
                <li>In connection with a business transaction (merger, acquisition, etc.)</li>
              </ul>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: colors.darkRed }}>
                6. Data Security
              </Typography>
              <Typography paragraph>
                We implement appropriate technical and organizational measures to protect your personal
                information against unauthorized or unlawful processing, accidental loss, destruction,
                or damage.
              </Typography>
              <ul>
                <li>End-to-end encryption using TLS 1.3 for data transmission</li>
                <li>AES-256 encryption for data at rest</li>
                <li>All data stored exclusively within the European Union</li>
                <li>24/7 security monitoring and automated threat detection</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Strict role-based access controls with comprehensive audit logging</li>
              </ul>
              <Typography paragraph>
                However, no method of transmission over the Internet or electronic storage
                is 100% secure.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: colors.darkRed }}>
                7. Data Retention
              </Typography>
              <Typography paragraph>
                We retain personal information for as long as necessary to fulfill the purposes outlined
                in this Privacy Policy, unless a longer retention period is required or permitted by law.
              </Typography>
              <Typography paragraph>
                <strong>Uploaded Files:</strong> Documents you upload are automatically deleted from our systems after 7 days to protect your privacy.
              </Typography>
              <Typography paragraph>
                <strong>Temporary Data:</strong> Deleted immediately after processing.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: colors.darkRed }}>
                8. Your Rights Under GDPR
              </Typography>
              <Typography paragraph>
                As an EU-based company, we fully comply with GDPR. You have the following rights regarding your personal information:
              </Typography>
              <ul>
                <li><strong>Access:</strong> Request access to your personal data</li>
                <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
                <li><strong>Erasure:</strong> Request deletion of your data</li>
                <li><strong>Restriction:</strong> Request restriction of processing</li>
                <li><strong>Portability:</strong> Request transfer of your data</li>
                <li><strong>Objection:</strong> Object to certain processing</li>
                <li><strong>Withdraw consent:</strong> Withdraw previously given consent</li>
              </ul>
              <Typography paragraph sx={{ mt: 2 }}>
                To exercise any of these rights, please contact us at info@ulpian.com
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: colors.darkRed }}>
                9. AI Act Compliance
              </Typography>
              <Typography paragraph>
                We are committed to complying with the EU AI Act requirements:
              </Typography>
              <ul>
                <li>Transparent AI system operations with clear explanations of processing</li>
                <li>Human oversight mechanisms for AI decision-making</li>
                <li>Regular risk assessments and mitigation strategies</li>
                <li>Comprehensive technical documentation and audit trails</li>
              </ul>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: colors.darkRed }}>
                10. Data Storage and Transfers
              </Typography>
              <Typography paragraph>
                All data is stored exclusively within the European Union for GDPR compliance. If any data needs to be transferred outside the EU, we ensure appropriate safeguards are in place for such transfers in accordance with GDPR requirements.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: colors.darkRed }}>
                11. Children's Privacy
              </Typography>
              <Typography paragraph>
                Our Service is not intended for individuals under the age of 18. We do not knowingly
                collect personal information from children under 18.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: colors.darkRed }}>
                12. Changes to This Policy
              </Typography>
              <Typography paragraph>
                We may update our Privacy Policy from time to time. We will notify you of any changes
                by posting the new Privacy Policy on this page and updating the "Last updated" date.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ mb: 2, color: colors.darkRed }}>
                13. Contact Information
              </Typography>
              <Typography paragraph>
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </Typography>
              <Typography paragraph>
                <strong>ULPIAN AI d.o.o.</strong><br />
                Zelinska 5<br />
                10000 Zagreb<br />
                Croatia<br /><br />
                Email:{' '}
                <Link
                  href="mailto:info@ulpian.com"
                  sx={{ color: colors.red }}
                >
                  info@ulpian.com
                </Link>
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
